import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export function Lightbox({ fotos, index, onClose, onNav }) {
  const touchX = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") onNav(1);
      else if (e.key === "ArrowLeft") onNav(-1);
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, onNav]);
  if (index == null) return null;

  // Swipe en celular: desliza con el dedo para pasar de foto.
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null || fotos.length < 2) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 45) onNav(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  // Se renderiza mediante portal a <body> para que el overlay cubra SIEMPRE toda la
  // pantalla, aunque la grilla esté dentro de secciones con transform (framer-motion),
  // que de otro modo romperían el position:fixed.
  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[90] flex items-center justify-center bg-ink/95 p-4"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      data-testid="lightbox"
    >
      <img src={fotos[index]} alt="" onClick={(e) => e.stopPropagation()} className="max-h-[85vh] max-w-full select-none object-contain" draggable={false} data-testid="lightbox-img" />
      <button
        onClick={onClose}
        className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-ink/60 text-sand backdrop-blur-sm transition-colors hover:bg-terra md:right-6 md:top-6"
        data-testid="lightbox-close-btn"
        aria-label="Cerrar"
      >
        <X size={26} strokeWidth={1.6} />
      </button>
      {fotos.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onNav(-1); }}
            className="absolute left-3 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-ink/60 text-sand backdrop-blur-sm transition-colors hover:bg-terra md:left-6 md:h-14 md:w-14"
            data-testid="lightbox-prev-btn"
            aria-label="Anterior"
          >
            <ChevronLeft size={30} strokeWidth={1.6} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onNav(1); }}
            className="absolute right-3 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-ink/60 text-sand backdrop-blur-sm transition-colors hover:bg-terra md:right-6 md:h-14 md:w-14"
            data-testid="lightbox-next-btn"
            aria-label="Siguiente"
          >
            <ChevronRight size={30} strokeWidth={1.6} />
          </button>
        </>
      )}
    </motion.div>,
    document.body
  );
}

// Galería interna — Regla universal del documento maestro (§3). Termina con botón VER GALERÍA → Fotos
export default function GaleriaGrid({ fotos, columnas = 5, testid = "galeria" }) {
  const [lb, setLb] = useState(null);
  const cols =
    columnas === 3
      ? "grid-cols-2 md:grid-cols-3"
      : "grid-cols-2 md:grid-cols-3 lg:grid-cols-5";

  return (
    <>
      <div className={`grid gap-3 md:gap-4 ${cols}`} data-testid={testid}>
        {fotos.map((f, i) => (
          <button
            key={f + i}
            type="button"
            onClick={() => setLb(i)}
            className="img-frame h-40 w-full md:h-48 lg:h-52"
            data-testid={`${testid}-foto-${i}`}
          >
            <img src={f} alt="" loading="lazy" className="h-full w-full object-cover" />
          </button>
        ))}
      </div>
      <AnimatePresence>
        {lb != null && <Lightbox fotos={fotos} index={lb} onClose={() => setLb(null)} onNav={(d) => setLb((v) => (v + d + fotos.length) % fotos.length)} />}
      </AnimatePresence>
    </>
  );
}